import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import VendorNotificationCard from "../components/VendorNotificationCard";

/* severity → colour + icon  */
const SEV = {
  danger:  { bg: 'bg-red-50',    icon: '🔴', badge: 'bg-red-100 text-red-700' },
  warning: { bg: 'bg-amber-50',  icon: '🟡', badge: 'bg-amber-100 text-amber-700' },
  info:    { bg: 'bg-blue-50',   icon: '🔵', badge: 'bg-blue-100 text-blue-700' },
};

function MessageWithProjectBold({ message }) {
  const msg = String(message || '');
  const m = msg.match(/^(.*?)(\|\s*Project:\s*|\.\s+Project:\s*)(.+)$/is);
  if (!m) return <>{msg}</>;
  const before = m[1];
  const label = m[2];
  const proj = (m[3] || '').trim();
  const parts = proj.split(' — ');
  const code = (parts[0] || '').trim();
  const name = parts.slice(1).join(' — ').trim();
  return (
    <>
      {before}
      <strong>{label.trim()}</strong>{' '}
      {name ? (
        <>
          <strong>{code}</strong> {' — '} <strong>{name}</strong>
        </>
      ) : (
        <strong>{code}</strong>
      )}
    </>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [sevFilter, setSevFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [stats, setStats]         = useState({ showing: 0, unread: 0, danger: 0, warning: 0 });
  // Vendor delivery notices are worked out fresh on every request and there are
  // hundreds of them, so a single list reads as a vendor list. The page offers them
  // apart: everything, what the system recorded, or vendors.
  const [source, setSource]       = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', 200);
      if (unreadOnly) params.set('unread', 'true');
      if (source !== 'all') params.set('source', source);
      const data = await api.get(`/notifications?${params}`);
      const all = Array.isArray(data) ? data : [];

      let filtered = all;
      if (sevFilter) filtered = filtered.filter(n => n.severity === sevFilter);

      setRows(filtered);
      // Vendor notices are worked out live on every request and carry is_read: 0
      // because there is no row to mark. Counting them as unread made this read
      // "50 unread" for ever — the merge hands the page a quarter of its limit in
      // vendor notices, so the number was just 200 / 4 and nothing could shift it.
      // Mark All Read cannot touch them, so they are not unread; they are standing.
      const clearable = all.filter(n => n.source !== 'vendor');
      setStats({
        showing: all.length,
        unread: clearable.filter(n => !n.is_read).length,
        danger: all.filter(n => n.severity === 'danger').length,
        warning: all.filter(n => n.severity === 'warning').length,
      });
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [sevFilter, unreadOnly, source]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    try { await api.post('/notifications/generate'); load(); } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-read', {});
      load();
      window.dispatchEvent(new CustomEvent('mrp:notifications-changed'));
    } catch {}
  };

  const markOne = async (id) => {
    try {
      await api.post('/notifications/mark-read', { notification_id: id });
      load();
      window.dispatchEvent(new CustomEvent('mrp:notifications-changed'));
    } catch {}
  };

  const dismiss = async (id) => {
    try { await api.del(`/notifications/${id}`); load(); } catch {}
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h1 className="page-title">Production reminders</h1>
          {stats.unread > 0 && (
            <span className="px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-red-500 text-white">
              {stats.unread} unread
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={generate} className="btn-secondary text-xs py-1.5 px-3">
            ↻ Refresh reminders
          </button>
          {stats.unread > 0 && (
            <button type="button" onClick={markAllRead} className="btn-secondary text-xs py-1.5 px-3">
              ✓ Mark All Read
            </button>
          )}
          <button type="button" onClick={() => navigate('/admin/email-settings')} className="btn-primary">
            ✉ Email Settings
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard label="Showing" value={stats.showing} color="bg-slate-100 text-slate-700" />
        <StatCard label="Unread" value={stats.unread} color="bg-blue-100 text-blue-700" />
        <StatCard label="Overdue (Critical)" value={stats.danger} color="bg-red-100 text-red-700" />
        <StatCard label="Due Soon (Warning)" value={stats.warning} color="bg-amber-100 text-amber-700" />
      </div>

      {/* Which notifications to show at all */}
      <div className="flex gap-1.5 flex-wrap">
        {[
          { key: 'all', label: 'All' },
          { key: 'production', label: 'Production' },
          { key: 'purchase', label: 'Purchase orders' },
          { key: 'vendor', label: 'Vendors' },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setSource(t.key)}
            className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors
              ${source === t.key
                ? 'bg-sap-blue text-white border-sap-blue'
                : 'bg-white text-sap-textLight border-sap-border hover:bg-sap-bg'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="card px-4 py-3 flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-2xs text-sap-textLight mb-0.5">Severity</label>
          <select value={sevFilter} onChange={e => setSevFilter(e.target.value)}
            className="h-7 w-36 px-1 text-xs border border-sap-border rounded bg-white">
            <option value="">All</option>
            <option value="danger">Overdue (Critical)</option>
            <option value="warning">Due Soon (Warning)</option>
          </select>
        </div>
        <label className="flex items-center gap-1 text-xs text-sap-text cursor-pointer select-none pb-0.5">
          <input type="checkbox" checked={unreadOnly} onChange={() => setUnreadOnly(v => !v)}
            className="accent-sap-blue w-3 h-3" />
          Unread only
        </label>
        <span className="ml-auto text-2xs text-sap-textLight">{rows.length} alert{rows.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Notification list */}
      <div className="bg-white rounded-xl shadow-sm border border-sap-border/60 overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sap-textLight italic text-xs">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sap-textLight italic text-xs">
            {source === 'vendor'
              ? 'No vendor notifications found.'
              : source === 'production'
                ? 'No production notifications found.'
                : source === 'purchase'
                  ? 'No purchase order notifications found.'
                  : 'No notifications found.'}
          </div>
        ) : (
          <div className="divide-y divide-sap-border/40">
            {rows.map((n, idx) => {
              if (n.type === "vendor_delivery_reminder") {
              console.log(n);
              }
              const s = SEV[n.severity] || SEV.warning;
              const prodLink =
                n.related_entity === 'production_orders' && n.related_id
                  ? `/production-order/${n.related_id}`
                  : null;
              const pcode = (n.project_code && String(n.project_code).trim()) || '';
              const pname = (n.project_name && String(n.project_name).trim()) || '';
              return (
                <div key={n.notification_id}
                  className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-50/80
                    ${!n.is_read ? s.bg : ''}`}>
                  <span className="text-sm mt-0.5 shrink-0">{s.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-slate-800">{n.title}</span>
                      {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                      {idx === 0 && (
                        <span className="text-[8px] font-bold uppercase text-amber-700 bg-amber-100 px-1 rounded">Latest</span>
                      )}
                      <span
  className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${s.badge}`}
>

  {n.type === "vendor_delivery_reminder"

    ? n.riskLevel

    : n.severity === "danger"

      ? "OVERDUE"

      : "DUE SOON"}

</span>
                    </div>
                    {n.type !== "vendor_delivery_reminder" && (

<div className="text-[10px] text-slate-600 mt-1 space-y-0.5">

  <div>
    <span className="text-slate-400">
      Project code:
    </span>

    <span className="font-semibold">
      {pcode || "—"}
    </span>
  </div>

  <div
    className="truncate"
    title={pname || undefined}
  >
    <span className="text-slate-400">
      Project name:
    </span>

    <span className="font-semibold">
      {pname || "—"}
    </span>
  </div>

</div>

)}
                    {n.type === "vendor_delivery_reminder" ? (

  <VendorNotificationCard
    notification={n}
  />

) : (

  <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
    <MessageWithProjectBold
      message={n.message || ""}
    />
  </p>

)}
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-[9px] text-slate-400">{fmtDate(n.created_at)}</span>
                      {prodLink && (
                        <button onClick={() => navigate(prodLink)}
                          className="inline-flex items-center gap-0.5 text-[10px] text-sap-blue hover:underline font-medium">
                          <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded
                                           bg-amber-400 text-white text-[7px]">▶</span>
                          Open production order
                        </button>
                      )}
                      {!n.is_read && (
                        <button onClick={() => markOne(n.notification_id)}
                          className="text-[10px] text-slate-400 hover:text-sap-blue">
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Dismiss / close button */}
                  <button onClick={() => dismiss(n.notification_id)} title="Dismiss notification"
                    className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${color}`}>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-[10px] font-medium opacity-70">{label}</div>
    </div>
  );
}